import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import {
  Select, SelectContent, SelectGroup, SelectItem, SelectLabel,
  SelectTrigger, SelectValue,
} from "../ui/select";

/**
 * Single-unit picker, for records that belong to exactly one territory (a
 * doctor sits in one place). `TerritoryPicker` is the multi-select sibling,
 * for people, who may cover several.
 *
 * Reads the master rather than accepting text: free text is what produced
 * "Patna" against four zones, and values no screen could act on.
 *
 * `valueField` NAMES WHICH IDENTITY THIS PICKER EMITS, and it is not
 * cosmetic. The two backends behind it take different fields: doctors take
 * `territory_unit` — a NAME, resolved server-side by `_validate_territory_unit`
 * — while distributors take `territory_unit_id`, a straight reference into
 * `settings_territories.id`. This component emitted the name unconditionally,
 * so once `routes_distributors` moved to the id reference the Add-distributor
 * dialog sent "Patna North" into an id field and every save was refused with
 * "That territory unit does not exist or is archived" for a unit that plainly
 * existed. The edit path failed the same way from the other side: it prefilled
 * the record's id, which matched no unit NAME, so a valid row rendered as
 * "Current (not an active unit)" showing a raw uuid.
 *
 * The default stays "name" because that is what the two doctor callers hold
 * today; a caller whose backend takes the reference passes `valueField="id"`.
 * Both branches are covered by TerritoryUnitSelect.test.jsx, and the
 * distributor contract is pinned end-to-end in DistributorsPage.territory.test.jsx.
 *
 * A value that is no longer an active unit — an archived unit, or a legacy
 * string on a record the migration flagged — is still rendered as an option so
 * the field shows what the record actually holds instead of appearing blank.
 * Picking anything else replaces it.
 */
export default function TerritoryUnitSelect({
  value,
  onChange,
  valueField = "name",
  testId = "territory-unit-select",
}) {
  const [units, setUnits] = useState([]);

  useEffect(() => {
    let cancelled = false;
    api.get("/settings/territories")
      .then(({ data }) =>
        !cancelled &&
        // `assignable` is the backend's own D10 boolean — HQ/Office refuses
        // doctors and distributors at the validator, so offering it here
        // would offer exactly what the save will refuse.
        setUnits((data.items || []).filter((u) => !u.archived && u.assignable !== false)))
      .catch(() => !cancelled && setUnits([]));
    return () => { cancelled = true; };
  }, []);

  const zones = units.filter((u) => u.kind === "patna_zone");
  const districts = units.filter((u) => u.kind !== "patna_zone");
  // Compare on the SAME field the picker emits. Comparing names while
  // emitting ids is what made a live distributor's own unit read as orphaned.
  const orphaned = value && !units.some((u) => u[valueField] === value);

  const item = (u) => (
    <SelectItem key={u.id} value={u[valueField]}>{u.name}</SelectItem>
  );

  return (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger className="h-11 focus:ring-callisto-gold" data-testid={testId}>
        <SelectValue placeholder="Pick a territory unit" />
      </SelectTrigger>
      <SelectContent>
        {orphaned && (
          <SelectGroup>
            <SelectLabel className="text-amber-700">Current (not an active unit)</SelectLabel>
            <SelectItem value={value}>{value}</SelectItem>
          </SelectGroup>
        )}
        {zones.length > 0 && (
          <SelectGroup>
            <SelectLabel>Patna zones</SelectLabel>
            {zones.map(item)}
          </SelectGroup>
        )}
        {districts.length > 0 && (
          <SelectGroup>
            <SelectLabel>Districts</SelectLabel>
            {districts.map(item)}
          </SelectGroup>
        )}
      </SelectContent>
    </Select>
  );
}
